import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail, API, getToken } from "../../lib/api";
import { Upload, Loader2, FileSpreadsheet, Trash2, Search, CheckCircle2, XCircle, AlertTriangle, Database } from "lucide-react";
import { toast } from "sonner";

export default function AdminNbrNames() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [testName, setTestName] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const fileRef = useRef(null);

  const loadStatus = () => {
    setLoading(true);
    api.get("/admin/nbr-names/status").then((r) => setStatus(r.data)).finally(() => setLoading(false));
  };
  useEffect(loadStatus, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/admin/nbr-names/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      if (!res.ok) {
        let msg = "Upload failed";
        try { const j = await res.json(); msg = j.detail || msg; } catch {}
        throw new Error(msg);
      }
      const j = await res.json();
      toast.success(`Loaded ${j.count} names from ${j.filename}.`);
      loadStatus();
      if (testName) runTest();
    } catch (err) {
      toast.error(err.message || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Delete the entire NBR protected-names register? This cannot be undone.")) return;
    try {
      await api.delete("/admin/nbr-names");
      toast.success("Register cleared.");
      loadStatus();
      setTestResult(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not clear.");
    }
  };

  const runTest = async () => {
    const q = testName.trim();
    if (q.length < 2) return;
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.get("/nbr-availability", { params: { name: q } });
      setTestResult(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Lookup failed.");
    } finally { setTesting(false); }
  };

  const formatDate = (iso) => {
    if (!iso) return "Never";
    try {
      return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
    } catch { return iso; }
  };

  return (
    <div className="p-6 md:p-10" data-testid="admin-nbr-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-7">
        <div>
          <p className="font-mono-brand text-[10px] text-[#447694] tracking-widest uppercase">Internal register</p>
          <h1 className="font-display text-3xl md:text-4xl text-[#04273d] font-semibold tracking-tight mt-1">NBR Protected Names</h1>
          <p className="text-sm text-[#447694] mt-1.5 max-w-2xl leading-relaxed">
            Upload an Excel (.xlsx) with one business name per row in <span className="font-semibold text-[#04273d]">column A</span>. Anyone whose name check overlaps with this list will see a cross on the free report and a "Speak to a specialist" CTA instead of "Buy now".
          </p>
        </div>
      </div>

      {/* Status card */}
      <div className="bnp-card p-6 md:p-7 mb-5" data-testid="admin-nbr-status">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-2xl bg-[#71edc9]/30 flex items-center justify-center">
              <Database size={20} className="text-[#04273d]" />
            </div>
            <div>
              <p className="font-mono-brand text-[10px] text-[#447694] tracking-widest uppercase">Currently loaded</p>
              <p className="font-display text-3xl text-[#04273d] font-bold leading-none mt-1" data-testid="admin-nbr-count">
                {loading ? "-" : (status?.count ?? 0).toLocaleString()}
              </p>
              <p className="text-xs text-[#447694] mt-1">names on the register</p>
            </div>
          </div>
          <div className="text-right text-xs text-[#447694] space-y-0.5">
            <p><span className="font-mono-brand uppercase tracking-wider text-[10px] text-[#447694]/80">Last upload</span> · <span className="text-[#04273d] font-medium">{formatDate(status?.last_uploaded_at)}</span></p>
            {status?.last_uploaded_by && <p>by {status.last_uploaded_by}</p>}
            {status?.last_filename && <p>from <span className="font-mono-brand">{status.last_filename}</span></p>}
          </div>
        </div>

        {status?.sample_names?.length > 0 && (
          <div className="mt-5 pt-5 border-t border-[#9ac7f7]/25">
            <p className="text-xs font-mono-brand tracking-widest uppercase text-[#447694] mb-2">Sample (first 5)</p>
            <div className="flex flex-wrap gap-1.5">
              {status.sample_names.map((n, i) => (
                <span key={i} className="text-xs bg-[#f7f7f7] border border-[#9ac7f7]/25 text-[#04273d] px-2.5 py-1 rounded-full">{n}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        {/* Upload */}
        <div className="bnp-card p-6 md:p-7">
          <div className="flex items-center gap-2.5 mb-3">
            <FileSpreadsheet size={18} className="text-[#71edc9]" />
            <p className="font-display font-semibold text-[#04273d] text-base">Upload new register</p>
          </div>
          <p className="text-xs text-[#447694] leading-relaxed mb-4">
            Drop your latest <span className="font-mono-brand">.xlsx</span> here. The existing register is <span className="font-semibold">replaced</span>, not appended, so each upload should be the complete current list.
          </p>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="w-full bg-[#f7f7f7] border-2 border-dashed border-[#9ac7f7]/50 hover:border-[#71edc9] rounded-2xl p-8 flex flex-col items-center gap-2 transition-colors disabled:opacity-50"
            data-testid="admin-nbr-upload"
          >
            {uploading ? <Loader2 size={22} className="animate-spin text-[#71edc9]" /> : <Upload size={22} className="text-[#447694]" />}
            <span className="font-display font-semibold text-sm text-[#04273d]">{uploading ? "Uploading…" : "Choose Excel file"}</span>
            <span className="text-[11px] text-[#447694]">.xlsx or .xlsm · column A · header row optional</span>
          </button>
          <input ref={fileRef} type="file" accept=".xlsx,.xlsm" onChange={onFile} className="hidden" data-testid="admin-nbr-file-input" />

          {status?.count > 0 && (
            <button
              type="button"
              onClick={clearAll}
              className="inline-flex items-center gap-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 px-3 py-2 rounded-full transition-colors mt-4"
              data-testid="admin-nbr-clear"
            >
              <Trash2 size={12} /> Clear the entire register
            </button>
          )}
        </div>

        {/* Test a lookup */}
        <div className="bnp-card p-6 md:p-7">
          <div className="flex items-center gap-2.5 mb-3">
            <Search size={18} className="text-[#71edc9]" />
            <p className="font-display font-semibold text-[#04273d] text-base">Test a name</p>
          </div>
          <p className="text-xs text-[#447694] leading-relaxed mb-4">
            Run any name against the register to see what a visitor would see on their report. Matching is suffix-insensitive (Ltd / Limited / PLC) and fuzzy (92%+ similarity).
          </p>
          <div className="flex gap-2">
            <input
              value={testName}
              onChange={(e) => setTestName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runTest()}
              placeholder="Type a business name"
              className="bnp-input flex-1"
              data-testid="admin-nbr-test-input"
            />
            <button type="button" onClick={runTest} disabled={testing || testName.trim().length < 2} className="btn-primary text-sm" data-testid="admin-nbr-test-button">
              {testing ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Check
            </button>
          </div>
          {testResult !== null && (
            <div className={`mt-4 rounded-2xl border px-4 py-3 flex items-center gap-3 ${testResult.available ? "bg-emerald-50 border-emerald-300/60" : "bg-red-50 border-red-300/60"}`} data-testid="admin-nbr-test-result">
              {testResult.available ? (
                <CheckCircle2 size={22} className="text-emerald-600 shrink-0" />
              ) : (
                <XCircle size={22} className="text-red-600 shrink-0" />
              )}
              <div className="min-w-0">
                <p className="font-display font-semibold text-[#04273d] text-sm">
                  {testResult.available ? "Available - visitor sees the tick + Buy now" : `Match - visitor sees the cross + Speak to a specialist`}
                </p>
                {testResult.matched && (
                  <p className="text-xs text-[#447694] mt-0.5 truncate">Matched against: <span className="italic">{testResult.matched}</span></p>
                )}
              </div>
            </div>
          )}
          {testResult === null && (status?.count ?? 0) === 0 && (
            <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-center gap-2 text-xs text-amber-700">
              <AlertTriangle size={14} /> Register is empty - every name will return "available" until you upload your list.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
